import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { pollPaymentStatus } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, AlertCircle } from "lucide-react";

const MAX_POLLS = 8;      // 8 × 2s ≈ 16s total
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "pending", payment_status: "pending" });
  const [attempts, setAttempts] = useState(0);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError("Missing session_id in URL.");
      return;
    }
    let cancelled = false;
    let attempt = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const s = await pollPaymentStatus(sessionId);
        if (cancelled) return;
        setState(s);
        setAttempts(attempt + 1);
        if (s.payment_status === "paid") return; // done
        if (["failed", "expired", "canceled"].includes(s.payment_status)) return;
        if (attempt >= MAX_POLLS - 1) return; // give up, show manual reload hint
        attempt += 1;
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.detail || "Could not confirm your payment.");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const paid = state.payment_status === "paid";
  const failed = ["failed", "expired", "canceled"].includes(state.payment_status);

  return (
    <div className="min-h-screen bg-[var(--fcc-cream)] flex items-center justify-center p-6" data-testid="payment-success-page">
      <div className="max-w-md w-full fcc-card p-8 text-center">
        {paid ? (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-[var(--fcc-green)]" />
            <h1 className="font-display text-3xl text-[var(--fcc-ink)] mt-4">You&apos;re now on KidHQ Pro</h1>
            <p className="text-sm text-[var(--fcc-ink-soft)] mt-2">
              Thanks for supporting KidHQ. Every feature is unlocked immediately.
            </p>
            <Link
              to="/app"
              data-testid="payment-success-return"
              className="fcc-btn-primary mt-6 inline-block text-sm py-2.5 px-6"
            >
              Take me back to my dashboard
            </Link>
          </>
        ) : failed || error ? (
          <>
            <XCircle className="w-12 h-12 mx-auto text-rose-500" />
            <h1 className="font-display text-2xl text-[var(--fcc-ink)] mt-4">Payment didn&apos;t complete</h1>
            <p className="text-sm text-[var(--fcc-ink-soft)] mt-2">
              {error || `Status: ${state.payment_status}. Nothing was charged — you can try again anytime.`}
            </p>
            <Link
              to="/app/settings/billing"
              data-testid="payment-failed-retry"
              className="fcc-btn-primary mt-6 inline-block text-sm py-2.5 px-6"
            >
              Try again
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-[var(--fcc-green)] animate-spin" />
            <h1 className="font-display text-2xl text-[var(--fcc-ink)] mt-4">Confirming your subscription…</h1>
            <p className="text-sm text-[var(--fcc-ink-soft)] mt-2">
              Stripe is finalizing the payment. This usually takes just a few seconds.
            </p>
            <p className="text-[10px] uppercase tracking-widest text-[var(--fcc-muted)] mt-4" data-testid="payment-poll-attempts">
              Attempt {attempts + 1} of {MAX_POLLS}
            </p>
            {attempts >= MAX_POLLS - 1 && (
              <div className="mt-6 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 text-left flex gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Still not confirmed. Stripe will finish this in the background —
                  refresh in a minute or check{" "}
                  <Link to="/app/settings/billing" className="underline">Billing</Link> to see the latest status.
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
